import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState(null);

  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  React.useEffect(() => {
    const storedAuthError = sessionStorage.getItem('authError');
    if (storedAuthError) {
      setAuthError(storedAuthError);
      sessionStorage.removeItem('authError');
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        showToast('Login successful! Welcome back.', 'success');
        navigate('/');
      } else {
        showToast(result.error || 'Login failed. Please try again.', 'error');
      }
    } catch {
      showToast('An unexpected error occurred. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#fafafa',
      '& fieldset': { borderColor: '#e8e8e8' },
      '&:hover fieldset': { borderColor: '#F6851F' },
      '&.Mui-focused fieldset': { borderColor: '#F6851F', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#F6851F' },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* ── Left: brand panel ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(160deg, #1c1c28 0%, #2a2a3e 60%, #1c1c28 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Soft glow behind logo */}
        <Box
          sx={{
            position: 'absolute',
            width: 480,
            height: 480,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(246,133,31,0.10) 0%, transparent 68%)',
            pointerEvents: 'none',
          }}
        />

        {/* Logo */}
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 6 }}>
          <img
            src="/logo_main.svg"
            alt="Examinax"
            style={{ width: 200, marginBottom: 28 }}
          />
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.35)',
              fontSize: '0.85rem',
              letterSpacing: '0.04em',
              lineHeight: 1.7,
            }}
          >
            IELTS Admin Platform
          </Typography>
        </Box>

        {/* Bottom subtle line */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent, #F6851F 40%, #E77525 60%, transparent)',
            opacity: 0.6,
          }}
        />
      </Box>

      {/* ── Right: form panel ── */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 460px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          px: { xs: 3, sm: 6 },
          py: 8,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 5, textAlign: 'center' }}>
            <img src="/logo_main.svg" alt="Examinax" style={{ width: 160 }} />
          </Box>

          {/* Heading */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.65rem',
              color: '#111',
              letterSpacing: '-0.025em',
              mb: 0.75,
            }}
          >
            Sign in
          </Typography>
          <Typography sx={{ color: '#aaa', fontSize: '0.875rem', mb: 4 }}>
            Welcome back. Enter your credentials below.
          </Typography>

          {authError && (
            <Alert
              severity="warning"
              onClose={() => setAuthError(null)}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {authError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isSubmitting}
              sx={{ ...fieldSx, mb: 2.5 }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              disabled={isSubmitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(p => !p)}
                      edge="end"
                      disabled={isSubmitting}
                      sx={{ color: '#bbb', '&:hover': { color: '#F6851F' } }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, mb: 3.5 }}
            />

            <Box
              component="button"
              type="submit"
              disabled={isSubmitting}
              sx={{
                width: '100%',
                py: '14px',
                border: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #F6851F 0%, #E77525 100%)',
                color: '#fff',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: isSubmitting ? 'none' : '0 4px 22px rgba(246,133,31,0.38)',
                transition: 'all 0.2s ease',
                opacity: isSubmitting ? 0.65 : 1,
                '&:hover:not(:disabled)': {
                  boxShadow: '0 6px 30px rgba(246,133,31,0.5)',
                  transform: 'translateY(-1px)',
                },
                '&:active:not(:disabled)': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 12px rgba(246,133,31,0.3)',
                },
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </Box>
          </form>

          <Typography
            sx={{ mt: 6, textAlign: 'center', fontSize: '0.72rem', color: '#ccc' }}
          >
            © {new Date().getFullYear()} Examinax. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
